/**
 * Centralized API Utility with Automatic Token Refresh
 *
 * Use this instead of raw fetch() for all authenticated API calls.
 * It automatically refreshes expired tokens and retries failed requests.
 */

import { refreshAccessToken, clearUserData, RefreshUnavailableError } from './tokenRefresh';
import { getConfig } from './config';
import { getToken as getAuthStoreToken, isLoggingOut } from '../stores/authStore';
import { setPending as setPendingAgreements } from '../stores/agreementGateStore';
import { sanitizeErrorMessage, apiErrorMessage } from './errorMessages';

const API_URL = getConfig('API_URL');

// fetch() wrapper that scrubs the URL/host out of network errors before
// they bubble up to UI code. Raw error stays in the console for devs.
async function safeFetch(url, init) {
  try {
    return await fetch(url, init);
  } catch (err) {
    console.error('apiRequest network error:', err);
    throw new Error(sanitizeErrorMessage(err));
  }
}

let isRefreshing = false;
let refreshSubscribers = [];

// Queue for requests waiting for token refresh
function subscribeTokenRefresh(callback) {
  refreshSubscribers.push(callback);
}

// Notify all waiting requests that token has been refreshed
function onRefreshComplete(newToken) {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
}

// Wake waiting requests with the reason renewal failed, so a rate-limited or
// briefly unreachable renewal surfaces as "try again" instead of as an expired
// session. Only a genuine auth failure passes no reason.
function onRefreshFailed(error) {
  refreshSubscribers.forEach(callback => callback(null, error));
  refreshSubscribers = [];
}

/**
 * Make an authenticated API request with automatic token refresh
 * @param {string} endpoint - API endpoint (e.g., '/admin/users' or full URL)
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} Fetch response
 */
export async function apiRequest(endpoint, options = {}) {
  // Build full URL if endpoint is relative
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;

  // Get current access token
  let accessToken = getAuthStoreToken();

  // Prepare request options with auth header
  const requestOptions = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`
    }
  };

  // Make the initial request
  let response = await safeFetch(url, requestOptions);

  // If token expired (401), attempt refresh and retry
  if (response.status === 401) {
    console.log('⚠️ Access token expired, attempting refresh...');

    // If already refreshing, wait for it to complete
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken, refreshError) => {
          if (!newToken) {
            reject(refreshError || new Error('Session expired. Please login again.'));
            return;
          }
          requestOptions.headers.Authorization = `Bearer ${newToken}`;
          resolve(safeFetch(url, requestOptions));
        });
      });
    }

    // Set refreshing flag
    isRefreshing = true;

    try {
      // Attempt to refresh token
      let newAccessToken = null;
      try {
        newAccessToken = await refreshAccessToken();
      } catch (refreshError) {
        // Renewal is rate limited or the API is briefly unreachable. The refresh
        // cookie is untouched, so the session is still good: fail this one
        // request and let the caller retry. Clearing state and bouncing to
        // /login here is what turned a throttled renewal into a forced
        // re-login (and a fresh 2FA prompt) for a whole office at once.
        if (refreshError instanceof RefreshUnavailableError) {
          console.warn('Token refresh temporarily unavailable, keeping session');
        }
        onRefreshFailed(refreshError);
        throw refreshError;
      }

      if (newAccessToken) {
        console.log('✅ Token refreshed successfully, retrying request');

        // Update authorization header with new token
        requestOptions.headers.Authorization = `Bearer ${newAccessToken}`;

        // Notify all waiting requests
        onRefreshComplete(newAccessToken);

        // Retry the original request with new token
        response = await safeFetch(url, requestOptions);
      } else {
        // Refresh failed - notify waiting subscribers then redirect
        console.error('❌ Token refresh failed, logging out');
        onRefreshComplete(null);
        clearUserData();
        // Don't show "session expired" when the user is intentionally logging
        // out — a trailing request just 401'd against the revoked session.
        if (!isLoggingOut()) window.location.href = '/login?expired=true';
        throw new Error('Session expired. Please login again.');
      }
    } finally {
      // Reset refreshing flag
      isRefreshing = false;
    }
  }

  // Agreement gate: any 423 Locked response carries the current pending list.
  // Populate the store so AgreementGateModal can render regardless of which
  // API call triggered it.
  if (response.status === 423) {
    try {
      const data = await response.clone().json();
      if (Array.isArray(data?.pending_agreements)) {
        setPendingAgreements(data.pending_agreements);
      }
    } catch { /* non-JSON body — ignore */ }
  }

  return response;
}

/**
 * Make an authenticated GET request
 * @param {string} endpoint - API endpoint
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiGet(endpoint, options = {}) {
  return apiRequest(endpoint, { ...options, method: 'GET' });
}

/**
 * Make an authenticated POST request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body data
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPost(endpoint, data, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Make an authenticated PATCH request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body data
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPatch(endpoint, data, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(data)
  });
}

/**
 * Make an authenticated PUT request
 * @param {string} endpoint - API endpoint
 * @param {object} data - Request body
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiPut(endpoint, data, options = {}) {
  return apiRequest(endpoint, {
    ...options,
    method: 'PUT',
    body: JSON.stringify(data)
  });
}

/**
 * Unwrap a fetch Response into parsed JSON, throwing on non-OK status.
 * Makes it convenient to use api helpers directly in TanStack Query queryFns:
 *   queryFn: () => apiGet('/foo').then(toJson)
 */
async function toJson(response) {
  if (!response.ok) {
    let detail = '';
    // Set by the API on a 5xx whose message was written for a reader (see
    // TrainingGenerationErrors). Without it, a 5xx body is treated as internal.
    let userFacing = false;
    try {
      const body = await response.json();
      const errs = body.errors;
      detail = body.error || body.message ||
        (Array.isArray(errs) ? errs.join(', ') : (errs && typeof errs === 'object' ? Object.values(errs).flat().join(', ') : errs)) || '';
      userFacing = body.user_facing === true;
    } catch {
      detail = await response.text().catch(() => '');
    }
    // Coerce to a string: a hash `errors` body must not reach the formatter as
    // an object (String(hash) is "[object Object]", which is what the UI used to
    // show for every server error with an empty or unexpected body).
    detail = typeof detail === 'string' ? detail : String(detail ?? '');
    // What the user reads is server copy only when the server wrote copy.
    // Internal errors and empty bodies become a status-appropriate line, and the
    // raw text is logged instead. Status and raw detail stay on the error object
    // for programmatic handling.
    const err = new Error(apiErrorMessage(response.status, detail, { userFacing }));
    err.status = response.status;
    err.detail = detail;
    throw err;
  }
  // Some endpoints return empty bodies (e.g., DELETE 204)
  const text = await response.text();
  return text ? JSON.parse(text) : null;
}

/**
 * JSON-returning variants — parse the response body and throw on non-OK.
 */
export async function apiGetJson(endpoint, options = {}) {
  return toJson(await apiGet(endpoint, options));
}
export async function apiPostJson(endpoint, data, options = {}) {
  return toJson(await apiPost(endpoint, data, options));
}
export async function apiPatchJson(endpoint, data, options = {}) {
  return toJson(await apiPatch(endpoint, data, options));
}
export async function apiPutJson(endpoint, data, options = {}) {
  return toJson(await apiPut(endpoint, data, options));
}
export async function apiDeleteJson(endpoint, options = {}) {
  return toJson(await apiDelete(endpoint, options));
}

/**
 * Make an authenticated DELETE request
 * @param {string} endpoint - API endpoint
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiDelete(endpoint, options = {}) {
  return apiRequest(endpoint, { ...options, method: 'DELETE' });
}

/**
 * Make an authenticated request with FormData (for file uploads)
 * @param {string} endpoint - API endpoint
 * @param {FormData} formData - FormData object
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>}
 */
export async function apiUpload(endpoint, formData, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  let accessToken = getAuthStoreToken();

  // Don't set Content-Type for FormData - browser sets it with boundary
  const requestOptions = {
    ...options,
    method: options.method || 'POST',
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${accessToken}`
    },
    body: formData
  };

  let response = await safeFetch(url, requestOptions);

  // Handle 401 with token refresh
  if (response.status === 401) {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken, refreshError) => {
          if (!newToken) {
            reject(refreshError || new Error('Session expired. Please login again.'));
            return;
          }
          requestOptions.headers.Authorization = `Bearer ${newToken}`;
          resolve(safeFetch(url, requestOptions));
        });
      });
    }

    isRefreshing = true;

    try {
      let newAccessToken = null;
      try {
        newAccessToken = await refreshAccessToken();
      } catch (refreshError) {
        // Transient renewal failure — see the note at the other call site.
        onRefreshFailed(refreshError);
        throw refreshError;
      }

      if (newAccessToken) {
        requestOptions.headers.Authorization = `Bearer ${newAccessToken}`;
        onRefreshComplete(newAccessToken);
        response = await safeFetch(url, requestOptions);
      } else {
        onRefreshComplete(null);
        clearUserData();
        if (!isLoggingOut()) window.location.href = '/login?expired=true';
        throw new Error('Session expired. Please login again.');
      }
    } finally {
      isRefreshing = false;
    }
  }

  return response;
}
